<script setup lang="ts">
import { useOrigin } from '~/shared/lib/useOrigin';

const props = defineProps<{
  type: string;
  id: number;
  dark?: boolean;
}>();

const $style = useCssModule();

const textClass = computed(() => [
  $style.socials__text,
  props.dark ? $style.socials__text_dark : '',
]);

const pageUrl = computed(() => `${useOrigin()}/${props.type}/${props.id}`);

const isCopied = ref(false);
let copyTimeout: ReturnType<typeof setTimeout> | null = null;

const handleCopyLink = async () => {
  try {
    await navigator.clipboard.writeText(pageUrl.value);
    isCopied.value = true;
    if (copyTimeout) clearTimeout(copyTimeout);
    copyTimeout = setTimeout(() => {
      isCopied.value = false;
    }, 2000);
  } catch {
    /* ничего не делаем, если запись в буфер обмена не удалась */
  }
};

onBeforeUnmount(() => {
  if (copyTimeout) clearTimeout(copyTimeout);
});
</script>

<template>
  <div :class="$style.socials">
    <div :class="textClass">Поделиться:</div>
    <div :class="$style.socials__links">
      <a
        :href="`https://telegram.me/share/url?url=${pageUrl}`"
        target="_blank"
        :class="[$style.socials__link, $style.socials__link_tg]"
      >
        <Icon
          :class="$style.socials__icon"
          :size="32"
          name="my-icon:i-social-tg"
        />
      </a>
      <a
        :href="`https://vk.com/share.php?url=${pageUrl}`"
        target="_blank"
        :class="[$style.socials__link, $style.socials__link_vk]"
      >
        <Icon
          :class="$style.socials__icon"
          :size="32"
          name="my-icon:i-social-vk"
        />
      </a>
      <button
        type="button"
        :class="$style.socials__copy"
        @click="handleCopyLink"
      >
        <Icon :class="$style.socials__copyIcon" name="my-icon:i-link" />
        <span :class="$style.socials__copyText">
          <span :class="$style.socials__copyLabel">{{
            isCopied ? 'Ссылка скопирована' : 'Скопировать ссылку'
          }}</span>
          <span :class="$style.socials__copyGhost" aria-hidden="true">
            Ссылка скопирована
          </span>
        </span>
      </button>
    </div>
  </div>
</template>

<style module lang="scss">
.socials {
  display: flex;
  flex-flow: row wrap;
  gap: var(--space-1-5x);
  align-items: center;

  &__text {
    @include text_caption1_caps;
    color: var(--color-gray-text);

    &_dark {
      color: var(--color-white);
    }
  }

  &__links {
    display: flex;
    flex-flow: row wrap;
    align-items: center;
    gap: var(--space-1-5x);
  }

  &__link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;

    rect {
      background: var(--color-gray-bg);
    }

    path {
      fill: var(--color-red-1);
    }
  }

  &__icon {
    cursor: pointer;

    :global(g rect) {
      fill: var(--color-gray-bg);
    }
  }

  &__copy {
    display: flex;
    box-sizing: border-box;
    flex-flow: row nowrap;
    align-items: center;
    gap: var(--space-1x);
    height: 32px;
    padding: 0 var(--space-2-5x);
    border: 1px solid var(--color-gray-line);
    background: var(--color-white);
    color: var(--color-black-1);
    cursor: pointer;
    white-space: nowrap;
    @include text_button3;
  }

  &__copyIcon {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    color: var(--color-black-1);
  }

  /* оба варианта текста лежат в одной grid-ячейке — ширина всегда по самому длинному, кнопка не дёргается */
  &__copyText {
    display: grid;
  }

  &__copyLabel,
  &__copyGhost {
    grid-area: 1 / 1;
  }

  &__copyGhost {
    visibility: hidden;
  }
}

@media (max-width: 479px) {
  .socials {
    &__copy {
      padding: 0 var(--space-2x);
    }
  }
}
</style>
